import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellRing, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { dashboardFuture } from '../api/livesDashboard';
import { startLive } from '../api/lives';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { LiveScheduleCountdown } from './LiveScheduleCountdown';

export const CreatorLiveReminder = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('lives');
  const { toast } = useToast();
  const [live, setLive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState('');
  useEffect(() => {
    if (!user?.id) return undefined;
    let active = true;
    const load = async () => {
      try {
        const rows = await dashboardFuture();
        const next = rows.find((l) => ['scheduled', 'waiting_tracks'].includes(l.status)
          && new Date(l.scheduled_at).getTime() - Date.now() <= 10 * 60000);
        if (active) setLive(next || null);
      } catch (_) { /* The notification list remains the durable reminder. */ }
    };
    load();
    const timer = setInterval(load, 30000);
    window.addEventListener('nymia:data-changed', load);
    document.addEventListener('visibilitychange', load);
    return () => { active = false; clearInterval(timer); window.removeEventListener('nymia:data-changed', load); document.removeEventListener('visibilitychange', load); };
  }, [user?.id]);
  const key = live ? `${live.id}:${live.scheduled_at}` : '';
  if (!live || key === dismissed || pathname === `/lives/${live.id}`) return null;
  const start = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (live.status === 'scheduled') await startLive(live.id);
      navigate(`/lives/${live.id}`);
    } catch (error) {
      toast({ title: t('schedule.start_error'), description: String(error.response?.data?.detail || error.message).split('|').pop(), variant: 'destructive' });
    } finally { setBusy(false); }
  };
  return <aside className="mx-4 mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-400/40 bg-amber-400/10 p-3" data-testid="creator-live-reminder">
    <BellRing className="h-5 w-5 shrink-0 gold-text" />
    <div className="min-w-0 flex-1">
      <p className="break-words text-sm font-semibold" data-testid="creator-live-reminder-title">{live.title}</p>
      {live.status === 'scheduled' ? <LiveScheduleCountdown scheduledAt={live.scheduled_at} isOwner testId="creator-live-reminder-countdown" className="text-xs gold-text" />
        : <p className="text-xs gold-text" data-testid="creator-live-reminder-starting">{t('schedule.starting')}</p>}
    </div>
    <Button onClick={start} disabled={busy} className="gold-gradient text-black" data-testid="creator-live-reminder-start">{t(live.status === 'scheduled' ? 'schedule.start' : 'schedule.open')}</Button>
    <Button size="icon" variant="ghost" onClick={() => setDismissed(key)} aria-label={t('schedule.dismiss')} data-testid="creator-live-reminder-dismiss"><X className="h-4 w-4" /></Button>
  </aside>;
};